(ns hai.journal2.timeline
  "Timeline styled components, entry renderers, filter panel, and pill components."
  (:require
   [clojure.string :as str]
   [hai.journal2.events :as events]
   [hai.journal2.fuzzy-search :as fuzzy-search]
   [hai.journal2.integration-registry :as reg]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]))

(def chevron-right-icon (icons/mui-icon :outlined :chevron_right))

;; ---------------------------------------------------------------------------
;; Helpers
;; ---------------------------------------------------------------------------

(defn- format-short-date [date-str]
  (when (seq date-str)
    (fu/format (fu/parse-local-date date-str) "MMM d")))

(defn format-time [inst]
  (when inst
    (let [d (js/Date. inst)
          h (.getHours d)
          m (.getMinutes d)]
      (str (when (< h 10) "0") h ":" (when (< m 10) "0") m))))

(defn format-compact-duration
  "Formats the difference between two instants as a compact string like '30m', '1h', '1h 30m'."
  [start-inst end-inst]
  (when (and start-inst end-inst)
    (let [total-min (fu/minutes-between (fu/local-date-time (.getTime (js/Date. start-inst)))
                                        (fu/local-date-time (.getTime (js/Date. end-inst))))]
      (when (pos? total-min)
        (let [h (quot total-min 60)
              m (rem total-min 60)]
          (cond
            (and (pos? h) (pos? m)) (str h "h " m "m")
            (pos? h)                (str h "h")
            :else                   (str m "m")))))))

(defn- entry-type-ident [entry]
  (get-in entry [:journal.entry/type :db/ident]))

(defn- linear-reference-label
  "Returns the Linear identifier or a source-type label."
  [entry]
  (or (:journal.entry.linear/identifier entry)
      (case (get-in entry [:journal.entry.linear/source-type :db/ident])
        :journal.entry.linear.source-type/project "Project"
        :journal.entry.linear.source-type/cycle "Cycle"
        :journal.entry.linear.source-type/project-milestone "Milestone"
        nil)))

(defn- linear-relevant-email
  "Returns the most relevant email for a Linear entry based on event type."
  [entry]
  (case (get-in entry [:journal.entry.linear/event-type :db/ident])
    :journal.entry.linear.event-type/issue-created
    (or (:journal.entry.linear/creator-email entry)
        (:journal.entry.linear/actor-email entry))

    :journal.entry.linear.event-type/issue-completed
    (or (:journal.entry.linear/assignee-email entry)
        (:journal.entry.linear/creator-email entry)
        (:journal.entry.linear/actor-email entry))

    :journal.entry.linear.event-type/project-created
    (or (:journal.entry.linear/creator-email entry)
        (:journal.entry.linear/actor-email entry))

    ;; cycle-completed, milestone-completed — org-wide, no single person
    nil))

(defn name->initials
  "Extracts up to two initials from a display name."
  [display-name]
  (when (seq display-name)
    (let [parts (str/split (str/trim display-name) #"\s+")]
      (str/upper-case
       (if (> (count parts) 1)
         (str (first (first parts)) (first (last parts)))
         (subs (first parts) 0 (min 2 (count (first parts)))))))))

;; ---------------------------------------------------------------------------
;; Filter panel styled components
;; ---------------------------------------------------------------------------

(o/defstyled filter-panel :div
  :rounded-lg :border :p-4
  {:border-color c/--gray-6
   :margin-bottom "0.5rem"})

(o/defstyled filter-section-label :div
  {:font-size "0.8125rem"
   :font-weight "600"
   :color c/--gray-11
   :margin-bottom "0.5rem"})

(o/defstyled filter-toggle-button :button
  :cursor-pointer :flex :items-center :gap-2
  {:font-size "0.875rem"
   :font-weight "500"
   :color t/--hai-dark-purple
   :background "transparent"
   :border "none"
   :padding "0.5rem 0"
   :margin-bottom "0.25rem"}
  [:&:hover {:opacity "0.8"}])

(o/defstyled pill-container :div
  :flex :flex-wrap :gap-2)

(o/defstyled pill-button :button
  :cursor-pointer :rounded-full :px-3 :py-1 :border
  {:font-size "0.8125rem"
   :font-weight "500"
   :border-color c/--gray-6
   :background "transparent"
   :color c/--gray-11
   :white-space "nowrap"
   :line-height "1.5"}
  [:&:hover {:border-color c/--gray-8}]
  [:&.active
   {:background t/--hai-dark-purple
    :color "white"
    :border-color t/--hai-dark-purple}])

(o/defstyled custom-popover-body :div
  :flex :flex-col :gap-3 :p-3
  {:min-width "200px"}
  [:label :flex :flex-col :gap-1
   {:font-size "0.8125rem" :font-weight "500" :color c/--gray-11}]
  [:input :border :rounded-md
   {:font-size "0.8125rem"
    :padding "0.375rem 0.5rem"
    :border-color c/--gray-6
    :outline "none"}]
  [:input:focus {:border-color t/--hai-dark-purple}])

(o/defstyled filter-controls-row :div
  :flex :items-center)

(def ^:private filter-icon (icons/mui-icon :outlined :filter_list))

(def ^:private date-presets
  [{:value :all-time   :label "All time"}
   {:value :today      :label "Today"}
   {:value :this-week  :label "This week"}
   {:value :this-month :label "This month"}
   {:value :last-30    :label "Last 30 days"}
   {:value :custom     :label "Custom"}])

(defn- custom-date-pill
  "Pill button that opens a popover with From/To date inputs."
  [active? custom-since custom-until]
  (let [label (if (and active? (seq custom-since) (seq custom-until))
                (str (format-short-date custom-since) " – " (format-short-date custom-until))
                "Custom")]
    [c/PopoverRoot
     [c/PopoverTrigger
      [:div [pill-button {:class (when active? "active")} label]]]
     [c/PopoverContent {:side "bottom" :align "start" :sideOffset 4}
      [custom-popover-body
       [:label "From"
        [:input {:type "date"
                 :value (or custom-since "")
                 :on-change (fn [e]
                              (let [v (.. e -target -value)]
                                (>e [:timeline/set-page-filter
                                     [[:date-range :since] v]]))
                              (>e [:timeline/set-page-filter
                                   [[:date-range :preset] :custom]]))}]]
       [:label "To"
        [:input {:type "date"
                 :value (or custom-until "")
                 :on-change (fn [e]
                              (let [v (.. e -target -value)]
                                (>e [:timeline/set-page-filter
                                     [[:date-range :until] v]]))
                              (>e [:timeline/set-page-filter
                                   [[:date-range :preset] :custom]]))}]]]]]))

;; ---------------------------------------------------------------------------
;; Integration modals (shared by page and manage-integrations modal)
;; ---------------------------------------------------------------------------

(def ^:private day-options
  [{:value 7    :label "7 days"}
   {:value 14   :label "14 days"}
   {:value 30   :label "30 days"}
   {:value 60   :label "60 days"}
   {:value 90   :label "90 days"}
   {:value 365  :label "1 year"}
   {:value 730  :label "2 years"}
   {:value 1095 :label "3 years"}
   {:value 1825 :label "5 years"}
   {:value 2920 :label "8 years"}
   {:value 4745 :label "13 years"}
   {:value 7665 :label "21 years"}])

(defn- connect-metadata [integration-type _]
  (let [backfill-days (reg/default-backfill-days integration-type)]
    {:after-connect   {:mutation 'integration/setup
                       :params   {:integration-type integration-type
                                  :backfill-days    backfill-days}}
     :frontend-action [:timeline/connect-complete
                       {:integration-type integration-type
                        :backfill-days    backfill-days}]}))

(defn- oauth-url-for [integration-type metadata]
  (reg/oauth-url integration-type metadata))

(defn not-connected-modal []
  (r/with-let [!type (<s :timeline/not-connected-type)
               !status (<s :timeline/connection-status)]
    (let [integration-type @!type
          enabled? (get-in @!status [integration-type :enabled?])]
      [c/DialogRoot
       {:open (some? integration-type)
        :onOpenChange (fn [open?]
                        (when-not open?
                          (>e [:timeline/close-not-connected])))}
       [c/DialogContent {:max-width "420px"}
        [c/DialogTitle (if enabled?
                         (str (when integration-type
                                (events/integration-type-label integration-type))
                              " disconnected")
                         (str "Connect " (when integration-type
                                           (events/integration-type-label integration-type))))]
        [c/dialog-close-x]
        [c/DialogDescription
         (if enabled?
           "This connection was removed. You can reconnect or disable the integration."
           "You need to connect this app before it can appear in your timeline.")]
        [c/Flex {:justify "end" :gap "3" :mt "4"}
         [c/DialogClose
          [c/Button {:variant "soft"} "Maybe later"]]
         (when-let [url (and integration-type
                             (oauth-url-for integration-type
                                            (connect-metadata integration-type nil)))]
           [:a {:href url :target "_top" :style {:text-decoration "none"}}
            [c/Button {} (if enabled? "Reconnect" "Connect")]])]]])))

(def ^:private calendar-picker-types
  [:timeline-integration.type/google-calendar
   :timeline-integration.type/outlook-calendar])

(defn- calendar-picker-icon [integration-type]
  (case integration-type
    :timeline-integration.type/google-calendar  [icons/google-calendar {:width 16 :height 16}]
    :timeline-integration.type/outlook-calendar [icons/outlook-calendar {:width 16 :height 16}]))

(def ^:private calendar-picker-add-icon (icons/mui-icon :outlined :add))
(def ^:private calendar-benefit-yes-icon (icons/mui-icon :outlined :check))
(def ^:private calendar-benefit-no-icon (icons/mui-icon :outlined :close))

(o/defstyled calendar-benefit-list :div
  :flex :flex-col
  {:gap "8px"
   :margin-top "12px"}
  [:.benefit-row :flex :items-center
   {:gap "8px"
    :font-size "14px"
    :color t/--charcoal-80}]
  [:.yes {:color "var(--green-9)"}]
  [:.no {:color "var(--red-9)"}])

(defn- calendar-benefit-row [yes? label]
  [:div.benefit-row
   [:span {:class (if yes? "yes" "no")}
    (if yes?
      [calendar-benefit-yes-icon {:style {:width "18px" :height "18px"}}]
      [calendar-benefit-no-icon {:style {:width "18px" :height "18px"}}])]
   [:span label]])

(defn- connectable-connections
  "Existing connections that are valid setup targets: not already linked to a
  timeline integration, and not sitting on a broken credential (reconnect for
  those lives elsewhere, not in this picker)."
  [connections]
  (remove #(or (some? (:ti-uuid %)) (:invalid? %)) connections))

(o/defstyled provider-group :div
  :flex :flex-col
  {:gap "8px"})

(o/defstyled provider-group-header :div
  :flex :items-center
  {:gap "8px"
   :font-size "14px"
   :font-weight "500"
   :color t/--charcoal-80})

(o/defstyled calendar-picker-list :div
  :flex :flex-col
  {:gap "16px"})

(defn- existing-connection-row
  "One row per existing (connectable) calendar connection — dispatches
  `integration/setup` targeted at that specific connection-uuid (multi-calendar:
  never silently reuse whichever connection of the type happens to exist).
  The provider is already named by the group header, so the row's label is
  just the account email."
  [integration-type {:keys [connection/uuid email]}]
  ^{:key (str uuid)}
  [c/Button {:size "3" :variant "soft"
             :style {:width "100%" :justify-content "flex-start"}
             :on-click #(do (>e [:timeline/setup-connection
                                 {:integration-type integration-type :connection-uuid uuid}])
                            (>e [:timeline/close-calendar-picker]))}
   email])

(defn- new-account-row
  "Always-present row — goes straight to OAuth for a brand new account.
  Rendered as a link styled to look like a button (via `asChild`) rather than
  an `<a>` wrapping a `<button>`, which is invalid HTML. The child must be a
  plain hiccup element: Radix's Slot merges its button classes onto the child
  via cloneElement, which works for DOM elements but not for reagent/ornament
  fn components (the classes would never reach the anchor)."
  [integration-type]
  (let [url (oauth-url-for integration-type (connect-metadata integration-type nil))]
    ^{:key (str "new-" integration-type)}
    [c/Button {:size "3" :variant "outline" :asChild true
               :style {:width "100%" :justify-content "flex-start"}}
     [:a {:href url :target "_top"
          :style {:text-decoration "none"}
          :on-click #(>e [:timeline/close-calendar-picker])}
      [calendar-picker-add-icon {:style {:width "16px" :height "16px"}}]
      "Connect new account"]]))

(defn- calendar-picker-provider-group [integration-type existing]
  ^{:key (str integration-type)}
  [provider-group
   [provider-group-header
    (calendar-picker-icon integration-type)
    (events/integration-type-label integration-type)]
   (doall (map (partial existing-connection-row integration-type) existing))
   [new-account-row integration-type]])

(defn calendar-picker-modal []
  (r/with-let [!open? (<s :timeline/calendar-picker-open?)
               !status (<s :timeline/connection-status)]
    [c/DialogRoot
     {:open (boolean @!open?)
      :onOpenChange (fn [open?]
                      (when-not open?
                        (>e [:timeline/close-calendar-picker])))}
     [c/DialogContent {:max-width "420px"}
      [c/DialogTitle "Connect your calendar"]
      [c/dialog-close-x]
      [c/DialogDescription
       "Connecting your calendar means you can:"]
      [calendar-benefit-list
       [calendar-benefit-row true "See your meetings and events in your timeline"]
       [calendar-benefit-row true "Add attendees into your contacts"]
       [calendar-benefit-row false "Create or modify events in your calendar"]]
      [c/Text {:size "1" :color "gray" :style {:display "block" :margin-top "12px"}}
       "All of your data is kept private and you can remove or modify your connections at any time."]
      [calendar-picker-list {:style {:margin-top "16px"}}
       (doall
        (for [it calendar-picker-types
              :let [existing (connectable-connections (get-in @!status [it :connections]))]]
          ^{:key (str it)}
          [calendar-picker-provider-group it existing]))]]]))

(defn github-username-modal []
  (r/with-let [!open?    (<s :timeline/github-username-modal-open?)
               !username (<s :timeline/github-username-input)]
    [c/DialogRoot
     {:open @!open?
      :onOpenChange (fn [open?]
                      (when-not open?
                        (>e [:timeline/close-github-username-modal])))}
     [c/DialogContent {:max-width "420px"}
      [c/DialogTitle "Your GitHub username"]
      [c/dialog-close-x]
      [c/DialogDescription
       "Enter your GitHub username to filter timeline entries to your own actions."]
      [c/Box {:py "3"}
       [c/TextFieldRoot
        {:placeholder "e.g. octocat"
         :value @!username
         :autoFocus true
         :on-change #(>e [:timeline/set-github-username-input (.. % -target -value)])
         :on-key-down (fn [e]
                        (when (= (.-key e) "Enter")
                          (>e [:timeline/save-github-username])))}]]
      [c/Flex {:justify "end" :gap "3" :mt "2"}
       [c/DialogClose
        [c/Button {:variant "soft"} "Cancel"]]
       [c/Button
        {:on-click #(>e [:timeline/save-github-username])}
        "Save"]]]]))

(defn- humanize-days [days]
  (cond
    (nil? days)    ""
    (>= days 365)  (let [years (js/Math.round (/ days 365.25))]
                     (str years " year" (when (not= 1 years) "s")))
    :else          (str days " days")))

(defn backfill-modal []
  (r/with-let [!open?    (<s :timeline/backfill-open?)
               !type     (<s :timeline/backfill-type)
               !days     (<s :timeline/backfill-days)
               !mode     (<s :timeline/backfill-mode)
               !loading? (<s :timeline/backfill-loading?)
               !result   (<s :timeline/backfill-result)]
    (let [integration-type @!type
          monitor?         (= :monitor @!mode)
          type-label       (when integration-type
                             (events/integration-type-label integration-type))]
      [c/DialogRoot
       {:open @!open?
        ;; In monitor mode the import must run to completion — block every
        ;; manual dismiss (Close button, Escape, overlay click). The modal
        ;; closes itself via `:timeline/backfill-check-done` when the backfill
        ;; finishes (or the server's staleness guard releases it).
        :onOpenChange (fn [open?]
                        (when (and (not open?) (not monitor?))
                          (>e [:timeline/close-backfill])))}
       [c/DialogContent
        (cond-> {:max-width "420px"}
          monitor? (assoc :onEscapeKeyDown    #(.preventDefault %)
                          :onPointerDownOutside #(.preventDefault %)
                          :onInteractOutside   #(.preventDefault %)))
        [c/DialogTitle (if monitor?
                         (str "Importing your " type-label " history")
                         (str "Import " type-label " History"))]
        [c/dialog-close-x]
        [c/DialogDescription
         (if monitor?
           "Sit tight — we're importing your history. This can take up to 2 minutes."
           (str "Import your " (when type-label (str type-label " "))
                "activity into your timeline."))]
        [c/Box {:py "4"}
         [c/Flex {:direction "column" :gap "3"}
          (if monitor?
            [c/Text {:size "2"}
             "Importing the last "
             [c/Badge {:color "blue" :variant "soft" :radius "full"}
              (humanize-days @!days)]
             "."]
            [:<>
             [c/Text {:size "2" :weight "medium"} "Time period"]
             [c/SelectRoot
              {:value (str @!days)
               :onValueChange #(>e [:timeline/set-backfill-days (js/parseInt %)])}
              [c/SelectTrigger {:placeholder "Select days..."}]
              [c/SelectContent
               (doall
                (for [{:keys [value label]} day-options]
                  ^{:key value}
                  [c/SelectItem {:value (str value)} label]))]]])
          (when @!loading?
            [c/Flex {:align "center" :gap "2" :py "2"}
             [c/Spinner {:size "1"}]
             [c/Text {:size "2" :color "gray"} "Importing..."]])
          (when @!result
            [c/Text {:size "2" :color (if (= @!result "No connection found.") "red" "green")}
             @!result])]]
        [c/Flex {:justify "end" :gap "3" :mt "4"}
         (cond
           ;; Monitor mode: no dismiss control — the modal auto-closes on
           ;; completion. (See onOpenChange/onEscapeKeyDown guards above.)
           monitor?
           nil

           (or @!loading? @!result)
           [c/DialogClose
            [c/Button {:variant "soft"} "Close"]]

           :else
           [:<>
            [c/DialogClose
             [c/Button {:variant "soft"} "Cancel"]]
            [c/Button {:on-click #(>e [:timeline/start-backfill])}
             "Import"]])]]])))

;; ---------------------------------------------------------------------------
;; Date range filter (page-level)
;; ---------------------------------------------------------------------------

(o/defstyled filter-column-scroll :div
  {:max-height "200px"
   :overflow-y "auto"
   :padding-left "4px"
   :padding-right "0.25rem"})

(o/defstyled filter-column-header :div
  :flex :items-center :justify-between :mb-2
  [:&.clickable :cursor-pointer]
  [:.clear {:font-size "0.75rem"
            :color t/--hai-dark-purple
            :cursor "pointer"
            :background "none"
            :border "none"
            :padding "0"}
   [:&:hover {:text-decoration "underline"}]])

(def search-icon (icons/mui-icon :outlined :search))
(def close-icon calendar-benefit-no-icon)

(o/defstyled filter-search-icon-btn :button
  :cursor-pointer :flex :items-center :justify-center
  {:background "transparent"
   :border "none"
   :padding "2px"
   :color "var(--gray-9)"
   :border-radius "var(--radius-2)"}
  [:&:hover {:color "var(--gray-11)"}])

(defn filter-column-with-search
  "Reusable filter column with a fuzzy search toggle in the header.
  Props:
    :title        - column heading string
    :show-clear?  - show Clear button?
    :on-clear     - fn for Clear click
    :fuse-opts    - {:keys [...]} for fuse.js, nil for plain strings
    :items        - full item collection
    :render-items - (fn [filtered-items] -> hiccup)
    :empty-label  - text when no items exist"
  [{:keys [title show-clear? on-clear fuse-opts items render-items empty-label]}]
  (r/with-let [!search-open? (r/atom false)
               !search-text  (r/atom "")]
    (let [search-open? @!search-open?
          search-text  @!search-text
          filtered     (fuzzy-search/fuzzy-filter items search-text (or fuse-opts {}))]
      [:div {:style {:flex "1" :min-width "0"}}
       [filter-column-header
        {:class (when-not search-open? "clickable")
         :on-click (when-not search-open?
                     #(reset! !search-open? true))}
        (if search-open?
          [c/TextFieldRoot
           {:value search-text
            :placeholder (str "Search " (str/lower-case title) "...")
            :autoFocus true
            :size "1"
            :on-change #(reset! !search-text (.. % -target -value))
            :on-key-down #(when (= "Escape" (.-key %))
                            (reset! !search-open? false)
                            (reset! !search-text ""))
            :style {:width "100%"}}
           [c/TextFieldSlot {:side "right"}
            [filter-search-icon-btn
             {:on-click #(do (reset! !search-open? false)
                             (reset! !search-text ""))}
             [close-icon {:style {:width "14px" :height "14px"}}]]]]
          [:<>
           [filter-section-label title]
           [:div {:style {:display "flex" :align-items "center" :gap "4px"}}
            (when show-clear?
              [:button.clear {:on-click (fn [e]
                                          (.stopPropagation e)
                                          (on-clear))}
               "Clear"])
            [:div {:style {:display "inline-flex" :align-items "center"}}
             [search-icon {:style {:width "14px" :height "14px" :color "var(--gray-9)"}}]]]])]
       [filter-column-scroll
        (if (seq items)
          (if (seq filtered)
            [render-items filtered]
            [c/Text {:size "2" :color "gray"} "No matches"])
          [c/Text {:size "2" :color "gray"} empty-label])]])))

(def people-source-icon reg/people-source-icon)

(o/defstyled active-filter-pill :div
  :inline-flex :items-center :rounded-full :py-1
  {:font-size "0.8125rem"
   :font-weight "500"
   :color c/--gray-12
   :background c/--gray-3
   :margin-left "1rem"
   :max-width "280px"
   :min-width 0
   :padding-left "0.75rem"
   :padding-right "0.25rem"
   :user-select "none"}
  [:.pill-label :overflow-hidden
   {:text-overflow "ellipsis" :white-space "nowrap" :min-width 0}]
  ([{:keys [label on-clear]}]
   [:<>
    [:span.pill-label label]
    [c/IconButton {:size "1" :variant "ghost" :radius "full" :color "gray"
                   :style {:width "18px" :height "18px" :flex-shrink 0 :cursor "pointer"
                           :margin-left "0.375rem"}
                   :on-click on-clear}
     [close-icon {:style {:width "14px" :height "14px"}}]]]))

(defn- summarize-names
  "Join up to 3 names, appending an ellipsis when there are more."
  [names]
  (let [names (vec names)]
    (str (str/join ", " (take 3 names))
         (when (> (count names) 3) ", …"))))

(defn date-range-filter
  "Page-level 3-column filter: types | domains | people.
  Columns B and C are mutually exclusive."
  []
  (r/with-let [!open?           (r/atom false)
               !page-filters    (<s :timeline/page-filters)
               !entity-filter   (<s :timeline/entity-filter)
               !domains         (<s :timeline/available-domains)
               !people          (<s :timeline/available-people)
               !current-user-person-id (<s :timeline/current-user-person-id)]
    (let [filters      @!page-filters
          preset       (get-in filters [:date-range :preset] :all-time)
          all-types    reg/all-types
          shown-types  (get filters :shown-types all-types)
          custom-since (get-in filters [:date-range :since])
          custom-until (get-in filters [:date-range :until])
          entity-filter @!entity-filter
          ef-column   (:column entity-filter)
          ef-selected (:selected entity-filter)
          domains     @!domains
          people      (into [{:id "__me__" :label "Me" :sources #{:humble} :me? true}]
                            (remove #(= (:id %) @!current-user-person-id) @!people))
          ;; Active-filter summary pills (beside the toggle button)
          types-active?      (not= (count shown-types) (count all-types))
          active-type-labels (for [[k label] reg/all-type-labels
                                   :when (contains? shown-types k)]
                               label)
          people-label       (into {} (map (juxt :id :label)) @!people)
          domain-label       (into {} (map (juxt :id :label)) domains)
          entity-names       (when (seq ef-selected)
                               (case ef-column
                                 :people (map #(if (= % :__me__) "Me" (get people-label % (str %))) ef-selected)
                                 :domain (map #(get domain-label % (str %)) ef-selected)
                                 nil))]
      [:<>
       [filter-controls-row
        [filter-toggle-button
         {:on-click #(swap! !open? not)}
         [filter-icon {:style {:width "16px" :height "16px"}}]
         (if @!open? "Hide filters" "Filters")
         (when (or (seq ef-selected)
                   (not= (count shown-types) (count all-types)))
           [c/Badge {:variant "solid" :size "1" :radius "full"
                     :style {:margin-left "4px"}}
            (+ (if (seq ef-selected) (count ef-selected) 0)
               (- (count all-types) (count shown-types)))])]
        (when types-active?
          [active-filter-pill
           {:label    (summarize-names active-type-labels)
            :on-clear #(>e [:timeline/set-page-filter [:shown-types all-types]])}])
        (when (seq ef-selected)
          [active-filter-pill
           {:label    (summarize-names entity-names)
            :on-clear #(>e [:timeline/clear-entity-filter])}])]
       (when @!open?
         [filter-panel
          ;; Date range row
          [:div {:style {:margin-bottom "0.75rem"}}
           [filter-section-label "Date Range"]
           [pill-container
            (doall
             (for [{:keys [value label]} date-presets]
               (let [active? (= value preset)]
                 ^{:key value}
                 (if (= value :custom)
                   [custom-date-pill active? custom-since custom-until]
                   [pill-button {:class (when active? "active")
                                 :on-click #(>e [:timeline/set-page-filter
                                                 [[:date-range :preset] value]])}
                    label]))))]]
          ;; 3-column row
          [c/Flex {:gap "6"}
           ;; Column A: Entry types
           [:div {:style {:flex "1" :min-width "0"}}
            [filter-section-label "Types"]
            [c/Flex {:direction "column" :gap "2"}
             (doall
              (for [[type-key label] reg/all-type-labels]
                ^{:key type-key}
                [:label {:style {:display "flex" :align-items "center" :gap "0.5rem" :cursor "pointer"}}
                 [c/Checkbox {:checked (contains? shown-types type-key)
                              :onCheckedChange
                              (fn [_]
                                (let [toggled   (if (contains? shown-types type-key)
                                                  (disj shown-types type-key)
                                                  (conj shown-types type-key))
                                      updated   (cond
                                                  (<= (count all-types) 1) shown-types
                                                  (empty? toggled)          (disj all-types type-key)
                                                  :else                     toggled)]
                                  (>e [:timeline/set-page-filter [:shown-types updated]])))}]
                 [c/Text {:size "2"} label]]))]]
           ;; Column B: Organizations — known contacts on top, then
           ;; auto-discovered domains (with a "+" action to save them as orgs).
           [filter-column-with-search
            {:title       "Organizations"
             :show-clear? (= :domain ef-column)
             :on-clear    #(>e [:timeline/clear-entity-filter])
             :fuse-opts   {:keys ["label" "id"]}
             :items       domains
             :empty-label "No organizations found"
             :render-items
             (fn [filtered-domains]
               (let [{contacts true others false}
                     (group-by (fn [d] (boolean (:contact? d))) filtered-domains)
                     row (fn [{:keys [id label sources contact? no-domain?] :as org}]
                           ^{:key id}
                           [:label {:style {:display "flex" :align-items "center" :gap "0.5rem" :min-width 0
                                            :cursor (if no-domain? "default" "pointer")
                                            :opacity (if no-domain? 0.6 1)}}
                            [c/Checkbox {:checked (and (= :domain ef-column) (contains? ef-selected id))
                                         :disabled (boolean no-domain?)
                                         :onCheckedChange #(when-not no-domain?
                                                             (>e [:timeline/select-domain id]))}]
                            [:span {:style {:display "inline-flex" :align-items "center" :gap "0.25rem"
                                            :flex 1 :min-width 0 :overflow "hidden"}}
                             [people-source-icon {:sources sources :directory? contact?}]
                             (let [display (if contact? label (str "@" label))]
                               [c/Tooltip {:content display}
                                [c/Text {:size "2"
                                         :style {:overflow "hidden"
                                                 :text-overflow "ellipsis"
                                                 :white-space "nowrap"
                                                 :min-width 0}}
                                 display]])
                             (when no-domain?
                               [c/Text {:size "1" :color "gray" :style {:flex-shrink 0}} "(no domain)"])]
                            (when-not contact?
                              [c/Tooltip {:content "Add to contacts"}
                               [c/IconButton
                                {:size "1" :variant "ghost"
                                 :style {:width "18px" :height "18px" :flex-shrink 0}
                                 :on-click (fn [e]
                                             (.stopPropagation e)
                                             (.preventDefault e)
                                             (>e [:contacts/create-from-virtual org]))}
                                [icons/plus {:width 12 :height 12}]]])])
                     section-label (fn [text]
                                     [c/Text {:size "1" :color "gray" :weight "bold"
                                              :style {:text-transform "uppercase"
                                                      :letter-spacing "0.05em"
                                                      :margin-top "0.25rem"}}
                                      text])]
                 [c/Flex {:direction "column" :gap "2"}
                  (when (seq contacts)
                    [:<>
                     [section-label "Contacts"]
                     (doall (for [o contacts] (row o)))])
                  (when (seq others)
                    [:<>
                     [section-label "Auto-discovered"]
                     (doall (for [o others] (row o)))])]))}]
           ;; Column C: People
           ;; Split: known contacts on top, then humble-directory people, then
           ;; auto-discovered (with a "+" action to save them as contacts).
           [filter-column-with-search
            {:title       "People"
             :show-clear? (= :people ef-column)
             :on-clear    #(>e [:timeline/clear-entity-filter])
             :fuse-opts   {:keys ["label" "id"]}
             :items       people
             :empty-label "No people found"
             :render-items
             (fn [filtered-people]
               (let [{contacts true other-people false}
                     (group-by (fn [p] (boolean (:contact? p))) filtered-people)
                     {dir-people true raw-people false}
                     (group-by (fn [p] (boolean (:directory? p))) (or other-people []))
                     row (fn [{:keys [id label sources directory? contact? me?] :as person}]
                           ^{:key id}
                           [:label {:style (cond-> {:display "flex" :align-items "center" :gap "0.5rem" :cursor "pointer" :min-width 0}
                                             me? (assoc :background "var(--accent-a3)"
                                                        :border-radius "var(--radius-2)"
                                                        :padding "2px 4px"
                                                        :margin-left "-4px"
                                                        :width "calc(100% + 4px)"))}
                            [c/Checkbox {:checked (and (= :people ef-column)
                                                       (contains? ef-selected (if me? :__me__ id)))
                                         :onCheckedChange #(>e (if me?
                                                                 [:timeline/select-me]
                                                                 [:timeline/select-person id]))}]
                            [:span {:style {:display "inline-flex" :align-items "center" :gap "0.25rem"
                                            :flex 1 :min-width 0 :overflow "hidden"}}
                             [people-source-icon {:sources sources :directory? (or contact? directory?)}]
                             [c/Tooltip {:content label}
                              [c/Text {:size "2"
                                       :style {:overflow "hidden"
                                               :text-overflow "ellipsis"
                                               :white-space "nowrap"
                                               :min-width 0}}
                               label]]]
                            (when (and (not contact?) (not me?))
                              [c/Tooltip {:content "Add to contacts"}
                               [c/IconButton
                                {:size "1" :variant "ghost"
                                 :style {:width "18px" :height "18px" :flex-shrink 0}
                                 :on-click (fn [e]
                                             (.stopPropagation e)
                                             (.preventDefault e)
                                             (>e [:contacts/create-from-virtual person]))}
                                [icons/plus {:width 12 :height 12}]]])])
                     section-label (fn [text]
                                     [c/Text {:size "1" :color "gray" :weight "bold"
                                              :style {:text-transform "uppercase"
                                                      :letter-spacing "0.05em"
                                                      :margin-top "0.25rem"}}
                                      text])]
                 [c/Flex {:direction "column" :gap "2"}
                  (when (seq contacts)
                    [:<>
                     [section-label "Contacts"]
                     (doall (for [p contacts] (row p)))])
                  (when (seq dir-people)
                    [:<>
                     [section-label "Workspace"]
                     (doall (for [p dir-people] (row p)))])
                  (when (seq raw-people)
                    [:<>
                     [section-label "Auto-discovered"]
                     (doall (for [p raw-people] (row p)))])]))}]]])])))

;; ---------------------------------------------------------------------------
;; Timeline styled components — spine-through-icons layout
;; ---------------------------------------------------------------------------

(o/defstyled timeline-container :div
  :relative :flex :flex-col
  ;; Spine line centered on the icon column:
  ;; time col (48px) + gap (12px) + half source circle (18px) = 78px
  [:&:before
   {:content "''"
    :position "absolute"
    :left "78px"
    :top "0"
    :bottom "0"
    :width "2px"
    :background c/--gray-5
    :transform "translateX(-50%)"}])

(o/defstyled date-marker :div
  :relative :grid :items-center :cursor-pointer :select-none
  {:grid-template-columns "48px 36px 1fr"
   :gap "0.75rem"
   :padding "0.75rem 0"}
  [:.marker-dot
   :flex :items-center :justify-center
   {:width "36px"
    :height "36px"
    :z-index "1"}
   [:.dot {:width "0.75rem"
           :height "0.75rem"
           :border-radius "50%"
           :background t/--hai-dark-purple}]]
  [:.label :flex :items-center
   {:gap "0.5rem"
    :font-size "1.125rem"
    :font-weight "600"
    :color t/--hai-dark-purple}]
  [:svg {:width "1.25rem"
         :height "1.25rem"
         :transition "transform 0.2s ease"}]
  [:&.open [:svg {:transform "rotate(90deg)"}]])

(o/defstyled collapsed-indicator :div
  :relative :grid
  {:grid-template-columns "48px 36px 1fr"
   :gap "0.75rem"
   :padding "0.25rem 0 0.5rem 0"}
  [:.dots :flex :flex-col :items-center :justify-center
   {:gap "3px"
    :width "36px"
    :z-index "1"}]
  [:.d {:width "4px"
        :height "4px"
        :border-radius "50%"
        :background c/--gray-8}])

(o/defstyled entry-row :div
  :relative :grid :items-start
  {:grid-template-columns "48px 36px 1fr"
   :gap "0.75rem"
   :padding "0.5rem 0"
   :max-width "960px"
   :border-radius "10px"
   :box-shadow "inset 0 0 0 1.5px transparent"
   :transition "background 0.35s ease, box-shadow 0.35s ease, transform 0.35s ease"}
  [:&.expandable {:cursor "pointer"}]
  [:&.hovered.expandable
   {:background t/--colour-snow
    :box-shadow "inset 0 0 0 1.5px rgba(71,5,208,0.1), 0 4px 16px rgba(30,8,93,0.08)"}]
  [:&.celebration
   {:background t/--property-32
    :box-shadow "inset 0 0 0 1.5px var(--property-33), 0 5px 0 var(--property-34), 0 8px 16px rgba(45,184,158,0.2)"
    :margin-top "0.75rem"
    :margin-bottom "0.75rem"}]
  [:&.celebration:hover
   {:box-shadow "inset 0 0 0 1.5px var(--property-33), 0 2px 0 var(--property-34), 0 4px 12px rgba(45,184,158,0.15)"
    :transform "translateY(3px)"}]
  [:.body-col
   :flex :items-start
   {:gap "0.5rem" :min-width "0"}
   [:>:last-child {:flex "1 1 0%" :min-width "0"}]]
  [:.expand-chevron
   :flex :items-center :justify-center :flex-shrink-0
   {:width "16px"
    :padding-top "0.4rem"
    :color t/--text-secondary-50
    :opacity "0.4"
    :transition "opacity 0.2s ease"}
   [:svg {:width "16px"
          :height "16px"
          :transition "transform 0.2s ease"}]]
  [:&.expandable:hover [:.expand-chevron {:opacity "1"}]]
  [:&.expanded [:.expand-chevron
                {:opacity "1"}
                [:svg {:transform "rotate(90deg)"}]]])

(o/defstyled entry-time :div
  :flex :flex-col :items-end :justify-center
  {:font-size "0.8125rem"
   :color t/--charcoal
   :font-variant-numeric "tabular-nums"
   :line-height "1.4"
   :padding-top "0.25rem"}
  [:.dur {:font-size "0.75rem"
          :color t/--text-secondary-50}])

(o/defstyled spine-icon :div
  :flex-shrink-0 :flex :items-center :justify-center
  {:width "36px"
   :height "36px"
   :border-radius "50%"
   :background c/--color-background
   :z-index "1"}
  [:svg {:width "20px" :height "20px"}])

;; Keep source-icon as alias for external consumers (e.g. collection.cljs)
(def source-icon spine-icon)

(o/defstyled compound-icon :div
  :relative :flex-shrink-0
  {:width "36px"
   :height "36px"
   :z-index "1"}
  [:.source
   :absolute :flex :items-center :justify-center
   {:right "0"
    :top "0"
    :width "36px"
    :height "36px"
    :border-radius "50%"
    :border-width "2px"
    :border-style "solid"
    :border-color t/--colour-chalk
    :z-index "1"}
   [:svg {:width "20px" :height "20px"}]]
  ;; Badge in bottom-right corner with a 2px knockout ring in the page bg
  ;; so it reads as an emblem placed on the source icon, not merged into it.
  ;; Single dark fill + white glyph + white ring — meaning lives in the
  ;; glyph; colour stays calm.
  [:.action
   :absolute :flex :items-center :justify-center
   {:right "-3px"
    :bottom "-3px"
    :width "16px"
    :height "16px"
    :border-radius "50%"
    :border-width "2px"
    :border-style "solid"
    :border-color c/--color-background
    :background t/--colour-ink
    :color t/--colour-chalk
    :box-sizing "content-box"
    :z-index "2"}
   [:svg {:width "14px"
          :height "14px"}]])

(o/defstyled entry-body :div
  :flex :flex-col
  {:gap "0.25rem" :min-width "0" :padding-top "0.25rem"})

(o/defstyled entry-subtitle :div
  {:font-size "0.8125rem"
   :color t/--text-secondary-50
   :line-height "1.4"})

(o/defstyled entry-meta :span
  {:font-size "0.75rem"
   :color t/--text-secondary-50})

(o/defstyled entry-link :a
  {:font-size "0.875rem"
   :font-weight "600"
   :color t/--hai-black-1
   :text-decoration "none"}
  [:&:hover {:text-decoration "underline"}])

(o/defstyled attendee-avatar :div
  :flex :items-center :justify-center :flex-shrink-0
  {:width "20px"
   :height "20px"
   :border-radius "50%"
   :background "var(--gray-4)"
   :color "var(--gray-11)"
   :font-size "10px"
   :font-weight "600"
   :line-height "1"})

(o/defstyled attendee-overflow attendee-avatar
  :cursor-pointer
  {:background "var(--gray-5)"})

(o/defstyled hover-reveal :div
  {:font-size "13px"
   :color t/--text-secondary-50
   :line-height "1.5"
   :opacity "0"
   :max-height "0"
   :overflow "hidden"
   :white-space "pre-wrap"
   :transition "opacity 0.35s ease, max-height 0.35s ease"}
  [:&.visible
   {:opacity "1"
    :max-height "1200px"}])

(o/defstyled smart-btn-outline :button
  :cursor-pointer
  {:background "transparent"
   :color t/--colour-ink
   :padding "8px 16px"
   :border-radius "20px"
   :border-width "1px"
   :border-style "solid"
   :border-color t/--colour-ink
   :font-size "13px"
   :font-weight "600"
   :transition "background 0.2s ease"
   :white-space "nowrap"}
  [:&:hover {:background t/--property-35}]
  [:&.compact
   {:padding "4px 10px"
    :font-size "11px"
    :border-radius "14px"}])

(o/defstyled identifier-chip :span
  {:font-size "11px"
   :background t/--colour-chalk
   :border-radius "4px"
   :padding "1px 5px"
   :color t/--text-secondary-50})

(o/defstyled source-type-label :div
  {:font-size "10px"
   :font-weight "700"
   :color t/--colour-purple
   :text-transform "uppercase"
   :letter-spacing "0.08em"})

(o/defstyled celebration-label :div
  {:font-size "10px"
   :font-weight "700"
   :color t/--colour-purple
   :text-transform "uppercase"
   :letter-spacing "0.08em"})

;; ---------------------------------------------------------------------------
;; Source type icons
;; ---------------------------------------------------------------------------

(def ^:private add-icon   (icons/mui-icon :round    :add))
(def ^:private check-icon (icons/mui-icon :round    :check))
(def ^:private alert-icon (icons/mui-icon :round    :priority_high))

(defn- action-glyph
  "Returns a small glyph character for the compound icon action circle."
  [entry]
  (let [entry-type (entry-type-ident entry)]
    (case entry-type
      :journal.entry.type/google-calendar add-icon
      :journal.entry.type/outlook-calendar add-icon
      :journal.entry.type/github
      (case (get-in entry [:journal.entry.github/event-type :db/ident])
        :journal.entry.github.event-type/pr-opened                add-icon
        :journal.entry.github.event-type/pr-merged                check-icon
        :journal.entry.github.event-type/review-approved          check-icon
        :journal.entry.github.event-type/review-changes-requested alert-icon
        nil)
      :journal.entry.type/linear
      (case (get-in entry [:journal.entry.linear/event-type :db/ident])
        :journal.entry.linear.event-type/issue-completed     check-icon
        :journal.entry.linear.event-type/issue-created       add-icon
        :journal.entry.linear.event-type/project-created     add-icon
        :journal.entry.linear.event-type/cycle-completed     check-icon
        :journal.entry.linear.event-type/milestone-completed check-icon
        nil)
      :journal.entry.type/tasks
      (case (get-in entry [:journal.entry.tasks/event-type :db/ident])
        :journal.entry.tasks.event-type/created   add-icon
        :journal.entry.tasks.event-type/completed check-icon
        nil)
      nil)))

(defn- entry-type-compound-icon
  "Returns a compound-icon with source circle (right) + action circle (left)."
  [entry]
  (let [entry-type (entry-type-ident entry)
        [icon color] (reg/entry-icon+color entry-type)
        src-bg (reg/source-color entry-type)]
    [compound-icon
     [:div.source {:style {:background src-bg :color color}}
      icon]
     (when-let [glyph (action-glyph entry)]
       [:div.action [glyph]])]))

;; ---------------------------------------------------------------------------
;; Entry helpers
;; ---------------------------------------------------------------------------

(defn- celebration-entry?
  "Returns true for milestone-completed events."
  [entry]
  (and (= :journal.entry.linear.source-type/project-milestone
          (get-in entry [:journal.entry.linear/source-type :db/ident]))
       (= :journal.entry.linear.event-type/milestone-completed
          (get-in entry [:journal.entry.linear/event-type :db/ident]))))

(defn- entry-source-link
  "Returns the navigable source URL for an entry, or nil."
  [entry]
  (reg/source-link entry))

(defn- priority-color [label]
  (case label
    "Urgent"     "var(--red-9)"
    "High"       t/--property-36
    "Medium"     t/--property-37
    "Low"        t/--text-secondary-50
    "No priority" nil
    nil))

(defn- linear-source-type-ident [entry]
  (get-in entry [:journal.entry.linear/source-type :db/ident]))

(defn- project-or-milestone? [entry]
  (#{:journal.entry.linear.source-type/project
     :journal.entry.linear.source-type/project-milestone}
   (linear-source-type-ident entry)))

;; ---------------------------------------------------------------------------
;; Entry body renderers
;; ---------------------------------------------------------------------------

(o/defstyled left-author-avatar :div
  :flex :items-center :justify-center :flex-shrink-0
  {:width "18px"
   :height "18px"
   :border-radius "50%"
   :background "var(--gray-3)"
   :color t/--text-secondary-50
   :font-size "8px"
   :font-weight "600"
   :line-height "1"
   :margin-top "2px"
   :align-self "flex-end"})

(defn- left-author-indicator [entry current-email]
  (when-let [author (:journal.entry/author-display-name entry)]
    (when (not= (:journal.entry/creator-email entry) current-email)
      (let [prefix (if (= (entry-type-ident entry) :journal.entry.type/notes)
                     "Shared by: "
                     "Integration from: ")]
        [c/Tooltip {:content (str prefix author)}
         [:div [left-author-avatar (name->initials author)]]]))))

(def ^:private note-title-max-chars 80)

(defn- note-title
  "First line of the note text, truncated with an ellipsis past
  `note-title-max-chars`."
  [text]
  (let [first-line (-> text str/split-lines first)]
    (if (> (count first-line) note-title-max-chars)
      (str (subs first-line 0 note-title-max-chars) "…")
      first-line)))

(defn- render-task [entry expanded? _celebration?]
  (let [title       (:journal.entry.tasks/title entry)
        description (:journal.entry.tasks/description entry)]
    [entry-body
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      [c/Text {:size "2" :weight "bold"} title]]
     (when (seq description)
       [hover-reveal {:class (when expanded? "visible")} description])]))

(defn- render-note [entry expanded? _celebration?]
  (let [text       (:journal.entry.notes/text entry)
        url        (:journal.entry.notes/url entry)
        edit-count (:journal.entry.notes/edit-count entry 1)
        tags       (seq (:journal.entry.notes/tags entry))
        title      (note-title text)]
    [entry-body
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      (if url
        [entry-link {:href url :target "_blank" :rel "noopener noreferrer"
                     :on-click #(.stopPropagation %)}
         title]
        [c/Text {:size "2" :weight "bold"} title])]
     [hover-reveal {:class (when expanded? "visible")} text]
     (when tags
       [c/Flex {:align "center" :gap "2" :wrap "wrap"}
        (for [t tags]
          ^{:key t}
          [entry-meta (str "#" (str/replace t #"^#+" ""))])])
     [c/Flex {:align "center" :gap "2"}
      (when (< 1 edit-count)
        [entry-subtitle (str edit-count " edits")])
      (when url
        [entry-meta (url/url->display-host url)])]]))

(defn- render-slack-dm
  [{:journal.entry.slack-dm/keys [text attachments id app-id team-id team-domain channel-id sender-id]
    :as entry}
   _hovered?
   _celebration?]
  [entry-body
   [c/Flex {:align "center" :gap "2"
            :on-click (fn [e]
                        (.stopPropagation e)
                        (js/window.open
                         (str "https://" (or team-domain "guanxiaiadvocates") ".slack.com/archives/" channel-id "/p" (str/replace id "." ""))
                         "_blank"))}
    [:div.slack_dm
     {:ref (fn [el]
             (when el
               (set! (.-innerHTML el) text)))}]]
   [entry-subtitle "Slack Memory"]
   (when-some [files (not-empty attachments)]
     (into [c/Flex {:align "left" :direction "column" :gap "1"}]
           (for [{:journal.entry.slack-dm.attachment/keys [file-id file-name permalink download mime-type thumb-480]}
                 files]
             (if (and file-id thumb-480 mime-type (str/starts-with? mime-type "image/"))
               [:a {:href permalink
                    :target "_blank"
                    :rel "noopener noreferrer"
                    :on-click #(.stopPropagation %)}
                [:img {:src (str "/slack/v1/file-proxy?file-id=" file-id)
                       :alt file-name
                       :style {:max-width "240px" :border-radius "4px" :cursor "pointer"
                               :box-shadow "0 2px 6px rgba(0,0,0,0.15)"}}]]
               [c/Link {:href (or download permalink)
                        :target "_blank"}
                [c/Text {:size "1"}
                 [:em file-name]]]))))])

(defn- attendee-list
  "Renders attendee avatars with tooltips. Shows first 3 individually, then +N overflow."
  [emails names]
  (r/with-let [!expanded? (r/atom false)]
    (let [pairs (map vector
                     emails
                     (concat names (repeat nil)))
          visible (take 3 pairs)
          overflow (drop 3 pairs)]
      [c/Flex {:align "center" :gap "1" :wrap "wrap"}
       (doall
        (for [[email display-name] visible]
          (let [label (or display-name (first (str/split email #"@")))
                initials (name->initials label)]
            ^{:key email}
            [c/Tooltip {:content (if display-name
                                   (str display-name " (" email ")")
                                   email)}
             [:div [attendee-avatar initials]]])))
       (when (seq overflow)
         (if @!expanded?
           [:<>
            (doall
             (for [[email display-name] overflow]
               (let [label (or display-name (first (str/split email #"@")))
                     initials (name->initials label)]
                 ^{:key email}
                 [c/Tooltip {:content (if display-name
                                        (str display-name " (" email ")")
                                        email)}
                  [:div [attendee-avatar initials]]])))]
           [attendee-overflow
            {:on-click #(swap! !expanded? not)}
            (str "+" (count overflow))]))])))

(defn- meet-button-visible?
  "Returns true when the Meet button should be shown: meet-link is present
   and current time is before end-at + 2 hours."
  [meet-link end-at]
  (and meet-link
       end-at
       (< (js/Date.now)
          (+ (.getTime (js/Date. end-at)) (* 2 60 60 1000)))))

(defn- render-google-calendar [entry expanded? _celebration?]
  (let [emails    (seq (:journal.entry.google-calendar/attendees entry))
        names     (or (:journal.entry.google-calendar/attendee-names entry) [])
        desc      (:journal.entry.google-calendar/description entry)
        link      (:journal.entry.google-calendar/link entry)
        meet-link (:journal.entry.google-calendar/meet-link entry)
        end-at    (:journal.entry.google-calendar/end-at entry)]
    [entry-body
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      (if link
        [entry-link {:href link :target "_blank" :rel "noopener noreferrer"
                     :on-click #(.stopPropagation %)}
         (:journal.entry.google-calendar/title entry)]
        [c/Text {:size "2" :weight "bold"}
         (:journal.entry.google-calendar/title entry)])]
     (when (seq desc)
       [hover-reveal {:class (when expanded? "visible")} desc])
     (when emails
       [c/Flex {:align "center" :gap "2"}
        [attendee-list (vec emails) names]])
     (when (meet-button-visible? meet-link end-at)
       [smart-btn-outline
        {:class    "compact"
         :style    {:align-self "start"}
         :on-click (fn [e]
                     (.stopPropagation e)
                     (js/window.open meet-link "_blank" "noopener,noreferrer"))}
        "Join Google Meet"])]))

(defn- render-outlook-calendar [entry expanded? _celebration?]
  (let [emails    (seq (:journal.entry.outlook-calendar/attendees entry))
        names     (or (:journal.entry.outlook-calendar/attendee-names entry) [])
        desc      (:journal.entry.outlook-calendar/description entry)
        link      (:journal.entry.outlook-calendar/link entry)
        meet-link (:journal.entry.outlook-calendar/meet-link entry)
        end-at    (:journal.entry.outlook-calendar/end-at entry)]
    [entry-body
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      (if link
        [entry-link {:href link :target "_blank" :rel "noopener noreferrer"
                     :on-click #(.stopPropagation %)}
         (:journal.entry.outlook-calendar/title entry)]
        [c/Text {:size "2" :weight "bold"}
         (:journal.entry.outlook-calendar/title entry)])]
     (when (seq desc)
       [hover-reveal {:class (when expanded? "visible")} desc])
     (when emails
       [c/Flex {:align "center" :gap "2"}
        [attendee-list (vec emails) names]])
     (when (meet-button-visible? meet-link end-at)
       [smart-btn-outline
        {:class    "compact"
         :style    {:align-self "start"}
         :on-click (fn [e]
                     (.stopPropagation e)
                     (js/window.open meet-link "_blank" "noopener,noreferrer"))}
        "Join Teams meeting"])]))

(defn- render-linear [entry expanded? celebration? _compact?]
  (let [link           (:journal.entry.linear/link entry)
        ref-label      (linear-reference-label entry)
        team-name      (:journal.entry.linear/team-name entry)
        title          (or (:journal.entry.linear/title entry)
                           ref-label
                           "Linear event")
        desc           (:journal.entry.linear/description entry)
        identifier     (:journal.entry.linear/identifier entry)
        priority-label (:journal.entry.linear/priority-label entry)
        p-color        (priority-color priority-label)
        source-type    (linear-source-type-ident entry)
        proj-or-ms?    (project-or-milestone? entry)
        assignee-email (linear-relevant-email entry)
        assignee-label (when assignee-email
                         (first (str/split assignee-email #"@")))]
    [entry-body
     ;; Celebration label
     (when celebration?
       [celebration-label "\uD83C\uDF89 MILESTONE COMPLETE"])
     ;; Source type label (PROJECT / MILESTONE) for non-celebration
     (when (and proj-or-ms? (not celebration?))
       [source-type-label
        (case source-type
          :journal.entry.linear.source-type/project "PROJECT"
          :journal.entry.linear.source-type/project-milestone "MILESTONE"
          nil)])
     ;; Title row with identifier chip and priority
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      (if link
        [entry-link {:href link :target "_blank" :rel "noopener noreferrer"
                     :on-click #(.stopPropagation %)}
         title]
        [c/Text {:size "2" :weight "bold"}
         title])
      (when (and identifier (not proj-or-ms?))
        [identifier-chip identifier])
      (when p-color
        [c/Flex {:align "center" :gap "1"}
         [:div {:style {:width "6px" :height "6px"
                        :border-radius "50%" :background p-color}}]
         [entry-meta priority-label]])]
     ;; Team name + assignee + author meta
     [c/Flex {:align "center" :gap "2"}
      (when team-name
        [entry-meta team-name])
      (when assignee-label
        [c/Tooltip {:content assignee-email}
         [:div [attendee-avatar (name->initials assignee-label)]]])]
     ;; Description
     (when (seq desc)
       [hover-reveal {:class (when expanded? "visible")} desc])]))

(defn- github-action-verb
  "Returns a human-readable action verb for a GitHub event type."
  [entry]
  (case (get-in entry [:journal.entry.github/event-type :db/ident])
    :journal.entry.github.event-type/pr-opened              "Opened"
    :journal.entry.github.event-type/pr-merged              "Merged"
    :journal.entry.github.event-type/review-approved        "Approved"
    :journal.entry.github.event-type/review-changes-requested "Changes requested"
    nil))

(defn- render-github [entry _expanded? _celebration? _compact?]
  (let [link      (:journal.entry.github/link entry)
        title     (or (:journal.entry.github/title entry) "GitHub event")
        badge     (github-action-verb entry)
        repo-name (:journal.entry.github/repo-name entry)
        actor     (:journal.entry.github/actor-login entry)]
    [entry-body
     [c/Flex {:align "center" :gap "2" :wrap "wrap"}
      (if link
        [entry-link {:href link :target "_blank" :rel "noopener noreferrer"
                     :on-click #(.stopPropagation %)}
         title]
        [c/Text {:size "2" :weight "bold"} title])
      (when badge
        [identifier-chip badge])
      (when repo-name
        [entry-meta repo-name])]
     (when actor
       [c/Flex {:align "center" :gap "2"}
        [entry-meta actor]])]))

(defn- render-entry-body [entry expanded? celebration? compact?]
  (case (entry-type-ident entry)
    :journal.entry.type/notes     [render-note entry expanded? celebration?]
    :journal.entry.type/tasks     [render-task entry expanded? celebration?]
    :journal.entry.type/slack-dm  [render-slack-dm entry expanded? celebration?]
    :journal.entry.type/google-calendar [render-google-calendar entry expanded? celebration?]
    :journal.entry.type/outlook-calendar [render-outlook-calendar entry expanded? celebration?]
    :journal.entry.type/linear [render-linear entry expanded? celebration? compact?]
    :journal.entry.type/github [render-github entry expanded? celebration? compact?]
    [entry-body [c/Text {:size "2" :color "gray"} "Unknown entry type"]]))

;; ---------------------------------------------------------------------------
;; Entry card (hover-aware wrapper)
;; ---------------------------------------------------------------------------

(defn- expandable?
  "Returns true when the entry has additional content hidden behind a
  click-to-expand. Entries without expandable content stay non-clickable."
  [entry]
  (case (entry-type-ident entry)
    :journal.entry.type/notes
    (let [text (str (:journal.entry.notes/text entry))]
      (not= text (note-title text)))

    :journal.entry.type/tasks
    (boolean (seq (:journal.entry.tasks/description entry)))

    :journal.entry.type/google-calendar
    (boolean (seq (:journal.entry.google-calendar/description entry)))

    :journal.entry.type/outlook-calendar
    (boolean (seq (:journal.entry.outlook-calendar/description entry)))

    :journal.entry.type/linear
    (boolean (seq (:journal.entry.linear/description entry)))

    false))

(defn- entry-card
  "Renders one timeline entry. Title click follows the entry's source link
  (handled inside each renderer); clicking anywhere else on the row toggles
  the description's expanded state when the entry has hidden content."
  [entry]
  (r/with-let [!hovered?       (r/atom false)
               !desc-expanded? (r/atom false)
               !email          (<s :auth/email)]
    (let [hovered?       @!hovered?
          desc-expanded? @!desc-expanded?
          celebration?   (celebration-entry? entry)
          expandable?    (and (not celebration?) (expandable? entry))
          dur            (when (reg/show-duration? (entry-type-ident entry))
                           (format-compact-duration
                            (:journal.entry/time entry)
                            (or (:journal.entry.google-calendar/end-at entry)
                                (:journal.entry.outlook-calendar/end-at entry))))]
      [entry-row
       {:class (cond-> []
                 hovered?       (conj "hovered")
                 celebration?   (conj "celebration")
                 expandable?    (conj "expandable")
                 desc-expanded? (conj "expanded"))
        :on-mouse-enter (when expandable? #(reset! !hovered? true))
        :on-mouse-leave (when expandable? #(reset! !hovered? false))
        :on-click (when expandable?
                    (fn [_e] (swap! !desc-expanded? not)))}
       [entry-time
        [:span (format-time (:journal.entry/time entry))]
        (when dur
          [:span.dur dur])
        [left-author-indicator entry @!email]]
       [:div
        [entry-type-compound-icon entry]]
       [:div.body-col
        (when expandable?
          [:div.expand-chevron [chevron-right-icon]])
        [render-entry-body entry desc-expanded? celebration? true]]])))

;; ---------------------------------------------------------------------------
;; Collapsible day section (timeline)
;; ---------------------------------------------------------------------------

(defn collapsible-day-section
  [{:keys [label default-open? entries]}]
  (r/with-let [!open? (r/atom default-open?)]
    [:<>
     [date-marker
      {:class (when @!open? "open")
       :on-click #(swap! !open? not)}
      [:div]
      [:div.marker-dot [:div.dot]]
      [:div.label
       [chevron-right-icon {}]
       [:span (str label " (" (count entries) ")")]]]
     (if @!open?
       (doall
        (for [entry entries]
          ^{:key (:journal.entry/uuid entry)}
          [entry-card entry]))
       [collapsed-indicator
        [:div]
        [:div.dots
         [:div.d] [:div.d] [:div.d]]
        [:div]])]))
